import { createElement, type ReactNode } from 'react';
import { toast } from 'sonner';
import type { NotificationStreamEvent } from './api.js';

// Toast renderer for incoming notifications. Maps notification priority
// to sonner intent + duration; respects the cta as an action button.
// Pulled out of notifications-stream.ts so it can be unit-tested in
// isolation (sonner is the only dep) without dragging EventSource in.

/** True for any route inside the onboarding wizard — `/onboarding/...` and
 *  `/brand/<slug>/onboarding/...` both. */
export function isOnboardingSurface(pathname: string): boolean {
  return /(^|\/)onboarding(\/|$)/.test(pathname);
}

/** The pathname to judge against, guarded so the module stays importable
 *  from a non-DOM context (it is pure-logic by design). */
function currentPathname(): string {
  return typeof window === 'undefined' ? '' : window.location.pathname;
}

/** Show a toast for an incoming notification. Returns the sonner id
 *  so callers can dismiss it programmatically if needed. */
export function showNotificationToast(
  n: NotificationStreamEvent,
  pathname: string = currentPathname(),
): string | number | null {
  // Priority 'low' is informational only — it lands in the inbox but
  // doesn't pop a toast. Founder reads it next time they open the
  // notifications page. Keeps urgent + high + normal traffic from
  // being drowned out.
  if (n.priority === 'low') return null;

  // [8140542] Nothing pops over the onboarding wizard.
  //
  // The server has a quiet window for this (preferences.ts) and it holds back
  // every channel but in_app, on a premise it states out loud: "the founder is
  // watching /home... the card is on /home and the bell is lit; for a founder
  // who is in the app, that IS the notification."
  //
  // Onboarding is the one time that premise is false. The founder IS in the
  // app and is NOT on /home, so the in_app notification does not arrive as a
  // card already on screen — it arrives as a toast over the step they are
  // filling in. A founder at step 6 of 6 got two, one of them telling them to
  // install the analytics tag that onboarding itself installs.
  //
  // Suppressed HERE and not in the quiet window, because the quiet window
  // drops a channel permanently (no digest, no catch-up). The notification
  // should still exist — the bell lights, the inbox has it, caches are
  // invalidated by the caller. Only the interruption is withheld.
  //
  // 'urgent' still gets through: suppressing everything would hide a real
  // alarm, and during onboarding an urgent is close to impossible anyway (no
  // campaigns, no spend), so the carve-out costs nothing.
  if (n.priority !== 'urgent' && isOnboardingSurface(pathname)) return null;

  const description = n.body.length > 200 ? `${n.body.slice(0, 200).trim()}…` : n.body;
  const action = n.cta
    ? {
        label: n.cta.label,
        onClick: () => {
          // Use window.location instead of router navigate so the toast
          // module stays free of router context — it's imported by
          // pure-logic callers (notifications-stream) that don't render.
          window.location.href = n.cta?.href ?? '/';
        },
      }
    : undefined;

  const options = action ? { description, action } : { description };
  const title = titleWithBrandBadge(n);

  // Map priority to sonner's intent + duration. Urgent gets the
  // longest dwell + the pulse-colored shadow (toaster.tsx maps
  // error → pulse). High → warning (spell-yellow). Normal → info.
  if (n.priority === 'urgent') {
    return toast.error(title, { ...options, duration: 12_000 });
  }
  if (n.priority === 'high') {
    return toast.warning(title, { ...options, duration: 8_000 });
  }
  // 'normal'
  return toast(title, { ...options, duration: 5_500 });
}

/** [founder-reported 2026-10-08] WHICH BRAND IS THIS ABOUT.
 *
 *  Two toasts both reading "i dug into your funnel", stacked, from two
 *  different brands. Nothing on either said which. The headline is written
 *  per-kind and is identical across brands by design, so the brand has to be
 *  said somewhere other than the headline.
 *
 *  A badge rather than a `brandname · ` title prefix (what slack and telegram
 *  do): those channels have nowhere to put a badge, a toast does. The badge
 *  carries the mark AND the name — the mark is the faster signal when four
 *  cards are stacked, the name is the unambiguous one — and it keeps the
 *  headline's full width for the headline.
 *
 *  Shape is BrandChip's, deliberately: the notifications panel already pills
 *  brand identity exactly this way (favicon in a circle, lowercased name,
 *  plum dot when there is no mark), and a founder who sees a toast and then
 *  opens the panel should be looking at the same object. Not imported from
 *  BrandChip because that component takes a hydrated `Brand` row, while all
 *  this module has is the two fields the stream carries — and this module is
 *  deliberately a .ts whose only dependency is sonner.
 *
 *  No badge when `brandName` is absent: the notification is org-scoped (or
 *  reached a client newer than the api), and an empty pill would be a worse
 *  signal than none. */
function titleWithBrandBadge(n: NotificationStreamEvent): ReactNode {
  if (!n.brandName) return n.title;
  const mark = n.brandFaviconUrl
    ? createElement('img', {
        src: n.brandFaviconUrl,
        alt: '',
        className: 'h-3 w-3 rounded-full border border-[var(--line)] object-cover',
      })
    : createElement('span', {
        'aria-hidden': true,
        className: 'inline-block h-1.5 w-1.5 rounded-full bg-[var(--plum)]',
      });
  const badge = createElement(
    'span',
    {
      key: 'badge',
      className: [
        'mb-1.5 inline-flex max-w-full items-center gap-1 self-start',
        'rounded-full border border-[var(--line)] bg-[var(--paper)] px-1.5 py-0.5',
        'font-mono text-[9px] font-semibold uppercase tracking-[0.08em]',
        'text-[var(--ink)]/75',
      ].join(' '),
    },
    mark,
    createElement('span', { className: 'truncate' }, n.brandName.toLowerCase()),
  );
  // A column, so the badge sits ON ITS OWN LINE above the headline rather
  // than indenting a headline that already wraps at 360px.
  return createElement('span', { className: 'flex flex-col' }, badge, n.title);
}
